/**
 * Detects code identifiers and raw ids in proof text, which a reviewer reading the PR cannot interpret.
 * The proof block is product language; `proofs attach` warns (never rejects) when the text is not.
 *
 * @module proof-language
 * @see packages/content/src/proofs-guide/overview.txt
 */

/** The shape of a prefixed entity id (wf_…, wrun_…): also what the proof block strips from failure reasons. */
export const PREFIXED_ID_SOURCE = String.raw`\b[a-z]{1,8}_[A-Za-z0-9]{16,}\b`;

/** The token kinds worth a warning. Lookarounds keep one kind from matching inside another (a sha inside a uuid). */
const IDENTIFIER_PATTERNS: RegExp[] = [
  // UUID
  /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi,
  // Prefixed entity id: wf_…, wrun_…, proof_… followed by a long encoded body
  new RegExp(PREFIXED_ID_SOURCE, 'g'),
  // Hex commit SHA: needs a digit and a letter, so ordinary words such as "decade" and plain numbers pass
  /(?<![\w-])(?=[0-9a-f]*\d)(?=[0-9a-f]*[a-f])[0-9a-f]{7,64}(?![\w-])/g,
  // camelCase or snake_case name assigned a value: triageEnabled=false, triage_enabled = true
  /\b(?:[a-z][a-z0-9]*(?:[A-Z][a-z0-9]*)+|[a-z][a-z0-9]*(?:_[a-z0-9]+)+)\s*={1,3}\s*[^\s,;.]+/g,
  // Bare camelCase field name of at least three words, not already reported as an assignment: externalTriageEnabled
  /\b[a-z][a-z0-9]*(?:[A-Z][a-z0-9]*){2,}\b(?!\s*=)/g,
];

const identifierTokens = (text: string): string[] => [
  ...new Set(IDENTIFIER_PATTERNS.flatMap((pattern) => [...text.matchAll(pattern)].map((match) => match[0]))),
];

/** One warning per identifier-looking token in the fields the agent wrote. */
export function proofLanguageWarnings(fields: { label: string; text: string | undefined }[]): string[] {
  return fields.flatMap(({ label, text }) =>
    text === undefined
      ? []
      : identifierTokens(text).map(
          (token) =>
            `${label} contains "${token}", which looks like a code identifier or id. Reviewers read this in product language: say what the user sees or what the product does instead (see \`cofactor proofs guide\`).`
        )
  );
}
